import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  CommercePartner,
  listenToUserCommercePartners,
  setCurrentPartner,
} from "../../../redux/commercePartnersSlice";
import { logout } from "../../../redux/authSlice";
import appLogo from "../../../assets/app/logo.png";

// ✅ Store sub-pages
import StoreProducts from "./StoreProducts";
import StoreOrders from "./StoreOrders";
import StoreCustomers from "./StoreCustomers";
import StoreGrowth from "./StoreGrowth";
import StoreDiscounts from "./StoreDiscounts";
import StoreFinance from "./StoreFinance";
import StoreAnalytics from "./StoreAnalytics";
import CommerceDeliveryZones from "../../SHOPPING/CommerceDeliveryZones";

import {
  Boxes, ShoppingCart, Users, TrendingUp, Tag, DollarSign, BarChart2,
  Search, ChevronDown, ChevronLeft, ChevronRight, Settings, LogOut, Plus, Store, X,
  Bike,
  LayoutDashboard,
  Pencil,
  MapPin,
} from "lucide-react";
import StoreHostOrders from "./StoreHostOrders";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as Profile.tsx / AtlasHome.tsx
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg:        "#FBF6EE",
  panelBg:   "#FFFFFF",
  panel2:    "#FFF9EF",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const SIDEBAR_W = 224;
const SIDEBAR_W_COLLAPSED = 72;

/* ═══════════════════════════════════════════════════════════
   CSS-in-JS — same conventions as AtlasHome's GLOBAL_CSS
═══════════════════════════════════════════════════════════ */
const SC_CSS = `
  .sc-root{font-family:'Montserrat','Cormorant Garamond',sans-serif;background:${HEART.bg};}
  .sc-root ::-webkit-scrollbar{width:8px;height:8px;}
  .sc-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .sc-root button{font-family:inherit;}

  @keyframes scRing{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes scPop{from{opacity:0;transform:translateY(8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes scWiggle{0%,100%{transform:rotate(-4deg);}50%{transform:rotate(4deg);}}
  @keyframes scBob{0%,100%{transform:translateY(0);}50%{transform:translateY(-4px);}}

  .sc-navitem{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.dark};font-weight:700;
    font-size:14px;text-align:left;transition:background .15s,transform .1s,box-shadow .15s;
  }
  .sc-navitem:hover{background:${HEART.goldSoft};}
  .sc-navitem.active{
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;
    box-shadow:0 6px 16px rgba(200,32,46,.35);
  }
  .sc-navitem:active{transform:scale(0.98);}
  .sc-navitem.collapsed{justify-content:center;padding:11px;}

  .sc-navicon{
    width:32px;height:32px;border-radius:11px;background:${HEART.goldSoft};display:flex;
    align-items:center;justify-content:center;flex-shrink:0;color:${HEART.redDark};transition:background .15s,color .15s;
  }
  .sc-navitem.active .sc-navicon{background:rgba(255,255,255,.25);color:#fff;}

  .sc-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.red};font-weight:700;
    font-size:13.5px;text-align:left;transition:background .15s;
  }
  .sc-logout:hover{background:${HEART.redSoft};}
  .sc-logout.collapsed{justify-content:center;padding:11px;}

  .sc-settings{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.textMuted};font-weight:700;
    font-size:13.5px;text-align:left;transition:background .15s;
  }
  .sc-settings:hover{background:${HEART.goldSoft};color:${HEART.dark};}
  .sc-settings.collapsed{justify-content:center;padding:11px;}

  .sc-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:10px;
    background:${HEART.goldSoft};border:none;color:${HEART.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s,transform .1s;
  }
  .sc-collapse-toggle:hover{background:${HEART.gold};color:#fff;}
  .sc-collapse-toggle:active{transform:scale(0.92);}

  .sc-panelcard{animation:scPop .25s ease both;}

  .sc-ringOuter{position:relative;display:inline-flex;border-radius:14px;flex-shrink:0;}
  .sc-ringBorder{
    position:absolute;inset:-2px;border-radius:16px;
    background:linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.red}, ${HEART.gold}, ${HEART.dark});
    background-size:300% 100%;animation:scRing 3s linear infinite;z-index:0;
  }
  .sc-ringInner{position:relative;z-index:1;background:#fff;border-radius:14px;padding:3px;display:inline-flex;}
  .sc-logoimg{width:30px;height:30px;border-radius:10px;object-fit:cover;display:block;}
  .sc-wiggle{animation:scWiggle 2.6s ease-in-out infinite;transform-origin:center;display:inline-flex;}
  .sc-bob{animation:scBob 1.6s ease-in-out infinite;}

  .sc-accountbtn{
    display:flex;align-items:center;gap:8px;border-radius:999px;border:1px solid ${HEART.border};
    background:#fff;padding:5px 14px 5px 5px;cursor:pointer;box-shadow:0 3px 10px rgba(27,14,6,.10);
    transition:box-shadow .15s,transform .1s;
  }
  .sc-accountbtn:hover{box-shadow:0 5px 14px rgba(27,14,6,.16);}
  .sc-accountbtn:active{transform:translateY(1px);}

  .sc-storerow{
    position:relative;display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    border-radius:14px;padding:9px 12px;cursor:pointer;text-align:left;transition:background .15s;
  }
  .sc-storerow:hover{background:${HEART.goldSoft};}
  .sc-storerow.active{background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});}
  .sc-storebadge{
    opacity:0;transform:translateX(-4px);transition:opacity .15s,transform .15s;
    font-size:9px;font-weight:800;letter-spacing:.05em;color:${HEART.redDark};
    background:${HEART.goldSoft};border:1px solid ${HEART.border};border-radius:999px;
    padding:2px 8px;flex-shrink:0;white-space:nowrap;
  }
  .sc-storerow:hover .sc-storebadge{opacity:1;transform:translateX(0);}
  .sc-storerow.active .sc-storebadge{background:rgba(255,255,255,.25);color:#fff;border-color:transparent;}

  .sc-createrow{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    border-radius:14px;padding:9px 12px;cursor:pointer;text-align:left;transition:background .15s;
  }
  .sc-createrow:hover{background:${HEART.goldSoft};}

  /* ── search ── */
  .sc-searchwrap{position:relative;}
  .sc-searchclear{
    display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:999px;
    background:${HEART.border};border:none;color:${HEART.textMuted};cursor:pointer;flex-shrink:0;
  }
  .sc-searchresults{
    position:absolute;top:calc(100% + 8px);left:0;right:0;background:#fff;border:1px solid ${HEART.border};
    border-radius:14px;box-shadow:0 14px 40px rgba(27,14,6,.16);z-index:60;overflow:hidden;padding:6px;
  }
  .sc-searchresultrow{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    border-radius:10px;padding:9px 10px;cursor:pointer;text-align:left;transition:background .12s;
    font-size:13px;font-weight:700;color:${HEART.dark};
  }
  .sc-searchresultrow:hover{background:${HEART.goldSoft};}
  .sc-searchempty{padding:12px 10px;font-size:12px;color:${HEART.textFaint};font-weight:600;}

  /* mobile bottom tab bar — fixed footer, first 5 tabs fill the view,
     remaining tabs reachable by horizontal scroll (scrollbar hidden) */
  .sc-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;overflow-x:auto;
    background:${HEART.panelBg};border-top:1px solid ${HEART.border};z-index:50;
    padding-bottom:env(safe-area-inset-bottom);box-shadow:0 -4px 16px rgba(27,14,6,0.08);
    scrollbar-width:none;
  }
  .sc-tabbar::-webkit-scrollbar{display:none;}
  .sc-tab{
    flex:0 0 20%;min-width:68px;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:3px;padding:9px 4px 10px;background:transparent;border:none;cursor:pointer;color:${HEART.textFaint};
    transition:color .15s;
  }
  .sc-tab.active{color:${HEART.red};}
  .sc-tab span{font-size:9px;font-weight:800;white-space:nowrap;}
  .sc-tabicon{
    width:30px;height:30px;border-radius:11px;display:flex;align-items:center;justify-content:center;
    background:transparent;transition:background .15s,color .15s;
  }
  .sc-tab.active .sc-tabicon{background:${HEART.redSoft};}

  .sc-desktop-only{display:none}
  .sc-mobile-only{display:block}
  @media(min-width:900px){
    .sc-desktop-only{display:flex !important}
    .sc-mobile-only{display:none !important}
    .sc-tabbar{display:none !important}
  }
`;

/* ═══════════════════════════════════════════════════════════
   TYPES + NAV
═══════════════════════════════════════════════════════════ */

type DashboardTab =
  | "products" | "orders" | "customers" | "growth"
  | "discounts" | "finance" | "analytics";

interface NavItem {
  id: DashboardTab;
  label: string;
  icon: React.ReactNode;
  keywords: string[];
}

const NAV_ITEMS: NavItem[] = [
  { id: "products",   label: "Products",   icon: <Boxes size={16} />,        keywords: ["products", "inventory", "catalog", "items", "stock"] },

  { id: "orders",     label: "Orders",     icon: <ShoppingCart size={16} />, keywords: ["orders", "sales", "purchase", "checkout"] },
  { id: "customers",  label: "Customers",  icon: <Users size={16} />,        keywords: ["customers", "clients", "buyers", "users"] },
  { id: "growth",     label: "Growth",     icon: <TrendingUp size={16} />,   keywords: ["growth", "marketing", "promo", "campaigns"] },
  { id: "discounts",  label: "Discounts",  icon: <Tag size={16} />,          keywords: ["discounts", "coupons", "deals", "sale", "promo code"] },
  { id: "finance",    label: "Finance",    icon: <DollarSign size={16} />,   keywords: ["finance", "payouts", "revenue", "earnings", "billing"] },
  { id: "analytics",  label: "Analytics",  icon: <BarChart2 size={16} />,    keywords: ["analytics", "stats", "reports", "insights", "charts"] },
];

/* helper — 2-letter initials fallback for stores without a logo image */
const storeInitials = (name?: string) =>
  (name || "My Store")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();


    /* ═══════════════════════════════════════════════════════════
   PER-CATEGORY DASHBOARD ROUTING — keyed off partner.subcategoryId,
   navigated with brandId (not userId). Only categories that own a
   dedicated dashboard are listed; everything else stays in StoreCentral.
═══════════════════════════════════════════════════════════ */
const getDashboardRoute = (
  subcategoryId: number | string | undefined | null,
  brandId: string,
  fallbackTab: string = "products"
): string => {
  switch (Number(subcategoryId)) {
    // ── Shopping (1) — food & fresh all share FoodDashboard ──
    case 101: // Grocery Stores
    case 102: // Fresh Markets
    case 103: // Bakeries
    case 104: // Butcher Shops
    case 105: // Seafood Markets
    case 106: // Beverage Shops
    case 107: // Organic Stores
    case 108: // Fast Foods
      return `/freshstore/${brandId}`;

    // ── Booking (2) ──
    case 201: return `/DriverDashboard/${brandId}`;      // Ride
    case 203: return `/shortdashboard/${brandId}`;       // Vacation Rentals
    case 204: return `/cleaningdashboard/${brandId}`;    // Cleanings
    case 205: return `/vacantdashboard/${brandId}`;      // Vacant Rentals
    case 206: return `/hoteldashboard/${brandId}`;       // Hotels
    case 207: return `/RestaurantDashboard/${brandId}`;  // Restaurants

    // ── Ticket (202) — parent + every sub-type share one dashboard ──
     case 202:   return `/TicketDashboard/${brandId}`;        // legacy / unspecified
    case 20201: return `/TrainDashboard/${brandId}`;         // Train
    case 20202: return `/BusDashboard/${brandId}`;           // Bus & Coach
    case 20203: return `/FlightDashboard/${brandId}`;        // Air
    case 20204: return `/FerryDashboard/${brandId}`;         // Ferry & Boat
    case 20205: return `/CinemaDashboard/${brandId}`;        // Cinema
    case 20206: return `/ThemeParkDashboard/${brandId}`;     // Theme Park
    case 20207: return `/ConcertDashboard/${brandId}`;       // Concerts
    case 20208: return `/SportsEventDashboard/${brandId}`;   // Sports Events
    case 20209: return `/AttractionDashboard/${brandId}`; 

    
    // ── Classifieds (3) ──
    case 301: return `/propertydashboard/${brandId}`;    // Properties
    case 302: return `/automotivedashboard/${brandId}`;  // Automotive

    // ── Everything else lives inside StoreCentral ──
    default: return `/store/${brandId}/${fallbackTab}`;
  }
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */

const StoreCentral: React.FC = () => {
  const { brandId, tab, listingId } = useParams<{ brandId: string; tab?: string; listingId?: string }>();  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const authLoaded = useSelector((state: RootState) => (state.auth as any).authLoaded ?? true);
  const { currentPartner, loading } = useSelector(
    (state: RootState) => state.commercePartners
  );
  /* owner-scoped only — `partners` is the platform-wide browse list */
  const partners = useSelector(
    (state: RootState) => (state.commercePartners as any).myPartners ?? []
  );
  const myPartnersLoaded = useSelector(
    (state: RootState) => (state.commercePartners as any).myPartnersLoaded ?? false
  );

  /* is this brand actually mine? */
  const ownsBrand = !brandId || partners.some((p: any) => p.brandId === brandId);

  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeTab = (tab as DashboardTab) || "products";

  /* signed out → nothing here is viewable */
  useEffect(() => {
    if (authLoaded && !currentUser?.userId) {
      navigate("/AuthSwitch", { replace: true });
    }
  }, [authLoaded, currentUser?.userId, navigate]);

  useEffect(() => {
    if (!currentUser?.userId) return;
    const unsub = dispatch(listenToUserCommercePartners(currentUser.userId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [currentUser?.userId, dispatch]);

  /* only ever promote a brand this user owns; anything else clears */
  useEffect(() => {
    if (!brandId || !myPartnersLoaded) return;
    const mine = partners.find((p: any) => p.brandId === brandId);
    dispatch(setCurrentPartner(mine ?? null));
  }, [brandId, partners, myPartnersLoaded, dispatch]);

  // Landing on /store with no brandId: send them to the first partner's
  // *correct* home — a Ride/Hotel/Property brand should never land on the
  // generic products tab.
  useEffect(() => {
    if (!brandId && partners.length > 0) {
      const first = partners[0];
      navigate(getDashboardRoute(first.subcategoryId, first.brandId, "products"), { replace: true });
    }
  }, [brandId, partners, navigate]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowAccountMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // ⌘K / Ctrl+K focuses the search input from anywhere on the page
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearchResults(true);
      }
      if (e.key === "Escape") {
        setShowSearchResults(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleTabChange = (tabId: DashboardTab) => {
    navigate(`/store/${brandId}/${tabId}`);
  };

  // Takes the whole partner now — destination depends on its
  // subcategoryId, not just its brandId.
  const handleStoreSwitch = (partner: any) => {
    setShowAccountMenu(false);
    navigate(getDashboardRoute(partner.subcategoryId, partner.brandId, activeTab));
  };

  // Creating a store = a fresh run through onboarding, keyed by user.
// Creating a store = a fresh run through onboarding, keyed by user.
  const handleCreateStore = () => {
    setShowAccountMenu(false);
    navigate(`/onboarding/${currentUser?.userId}`);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  // Search matches nav sections by label + keywords. Small, dependency-free
  // fuzzy-ish match: case-insensitive substring against label + keyword list.
  const searchResults = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return NAV_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.includes(q))
    );
  })();

  const goToSearchResult = (item: NavItem) => {
    handleTabChange(item.id);
    setSearchQuery("");
    setShowSearchResults(false);
    searchInputRef.current?.blur();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchResults.length > 0) {
      goToSearchResult(searchResults[0]);
    }
  };

  const renderComponent = () => {
    switch (activeTab) {
      case "products":  return <StoreProducts brandId={brandId!} listingId={listingId} />;     
      case "orders":    return <StoreHostOrders brandId={brandId!} />;
      case "customers": return <StoreCustomers brandId={brandId!} />;
      case "growth":    return <StoreGrowth brandId={brandId!} />;
      case "discounts": return <StoreDiscounts brandId={brandId!} />;
      case "finance":   return <StoreFinance brandId={brandId!} />;
      case "analytics": return <StoreAnalytics brandId={brandId!} />;
      default:          return <StoreProducts brandId={brandId!} />;
    }
  };

  /* store logo tile — used for the account button + every row in the switcher */
  const StoreTile: React.FC<{ partner: any; size?: number }> = ({ partner, size = 30 }) => {
    const logo = partner?.image || partner?.brandLogo || partner?.logoUrl || partner?.logo;
    return logo ? (
      <img
        src={logo}
        alt={partner?.name || "Store"}
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          el.onerror = null;
          el.style.display = "none";
          const fallback = el.nextElementSibling as HTMLElement | null;
          if (fallback) fallback.style.display = "flex";
        }}
        style={{ width: size, height: size, borderRadius: size * 0.32, objectFit: "cover", flexShrink: 0, border: `1px solid ${HEART.border}` }}
      />
    ) : (
      <span
        style={{
          width: size, height: size, borderRadius: size * 0.32, display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${HEART.gold}, ${HEART.goldDark})`, color: "#fff",
          fontWeight: 800, fontSize: size * 0.4, flexShrink: 0,
        }}
      >
        {storeInitials(partner?.name)}
      </span>
    );
  };
/* ── waiting on auth or the ownership list ── */
  if (!authLoaded || (currentUser?.userId && !myPartnersLoaded)) {
    return (
      <div className="sc-root" style={S.loaderPage}>
        <style>{FONT_CSS + SC_CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div className="sc-bob" style={S.loaderBubble}><Store size={22} color="#fff" /></div>
          <p style={{ color: HEART.textMuted, fontWeight: 700, marginTop: 14 }}>Checking your access…</p>
        </div>
      </div>
    );
  }

  /* ── signed in, but this brand isn't theirs ── */
  if (brandId && !ownsBrand) {
    return (
      <div className="sc-root" style={S.loaderPage}>
        <style>{FONT_CSS + SC_CSS}</style>
        <div className="sc-panelcard" style={S.emptyCard}>
          <p style={{ fontSize: 20, fontWeight: 700, color: HEART.dark, marginBottom: 8, fontFamily: "'Cormorant Garamond', serif" }}>
            This store isn't yours
          </p>
          <p style={{ fontSize: 13, color: HEART.textMuted, marginBottom: 20, maxWidth: 300 }}>
            Seller Studio only opens stores on your own account. Check you're signed in with the right one.
          </p>
          <button style={S.primaryBtn} onClick={() => navigate(partners.length ? "/store" : `/onboarding/${currentUser?.userId}`)}>
            {partners.length ? "Go to my stores" : "Create a store"}
          </button>
        </div>
      </div>
    );
  }
  /* ── loading / empty states ── */
  if (loading && !currentPartner) {
    return (
      <div className="sc-root" style={S.loaderPage}>
        <style>{FONT_CSS + SC_CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div className="sc-bob" style={S.loaderBubble}>
            <Store size={22} color="#fff" />
          </div>
          <p style={{ color: HEART.textMuted, fontWeight: 700, marginTop: 14 }}>
            Setting up your store…
          </p>
        </div>
      </div>
    );
  }

  if (!currentPartner) {
    return (
      <div className="sc-root" style={S.loaderPage}>
        <style>{FONT_CSS + SC_CSS}</style>
        <div className="sc-panelcard" style={S.emptyCard}>
          <p style={{ fontSize: 20, fontWeight: 700, color: HEART.dark, marginBottom: 16, fontFamily: "'Cormorant Garamond', serif" }}>
            No store found yet 🌱
          </p>
          <button style={S.primaryBtn} onClick={() =>  navigate(`/onboarding/${currentUser?.userId}`)}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }



  /* store switcher card — 3D style matching CommerceOnboarding's PartnerCard */
const StoreSwitchCard: React.FC<{ partner: any; isCurrent: boolean }> = ({ partner, isCurrent }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 12,
      background: "#fff", borderRadius: 16, border: `1.5px solid ${HEART.border}`,
      boxShadow: "0 4px 0 #E7D9B8, 0 8px 20px rgba(27,14,6,.08)",
      padding: "12px 14px", marginBottom: 10,
    }}
  >
    <div style={{ width: 3, height: 46, background: HEART.red, flexShrink: 0, borderRadius: 2 }} />
    <StoreTile partner={partner} size={44} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: HEART.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {partner.name || "Store"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
        {partner.address?.city && (
          <span style={{ fontSize: 10, color: HEART.textMuted, display: "flex", alignItems: "center", gap: 3 }}>
            <MapPin size={9} /> {partner.address.city}
          </span>
        )}
        {isCurrent && (
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "#fff", background: HEART.red, padding: "2px 8px", borderRadius: 999 }}>
            CURRENT
          </span>
        )}
      </div>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
      <button
        style={{ background: `linear-gradient(135deg, ${HEART.gold}, ${HEART.goldDark})`, border: "none", color: "#fff", padding: "7px 12px", cursor: "pointer", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5, borderRadius: 8, whiteSpace: "nowrap" }}
        onClick={() => handleStoreSwitch(partner)}
      >
        <LayoutDashboard size={11} /> DASHBOARD
      </button>
      <button
        style={{ background: "#fff", border: `1.5px solid ${HEART.border}`, color: HEART.dark, padding: "7px 12px", cursor: "pointer", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5, borderRadius: 8, whiteSpace: "nowrap" }}
        onClick={() => { setShowAccountMenu(false); navigate(`/onboarding/${partner.brandId}`); }}
      >
        <Pencil size={11} /> EDIT
      </button>
    </div>
  </div>
);

  return (
    <div className="sc-root" style={S.page}>
      <style>{FONT_CSS + SC_CSS}</style>

      {/* ═══ TOP BAR — logo + search + account only, no tab name clutter ═══ */}
      <div style={S.topbar}>
        <div style={S.brandRow}>
          <span className="sc-ringOuter">
            <span className="sc-ringBorder" />
            <span className="sc-ringInner">
              <span className="sc-wiggle">
                <img
                  src={appLogo}
                  alt="Heartlas"
                  className="sc-logoimg"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              </span>
            </span>
          </span>
          <div className="sc-desktop-only" style={{ flexDirection: "column" }}>
            <div style={S.brandName}>Heartlas</div>
            <div style={S.brandSub}>Seller Studio</div>
          </div>
        </div>

        {/* live search — desktop only */}
        <div className="sc-desktop-only sc-searchwrap" style={{ flex: 1, maxWidth: 340 }} ref={searchRef}>
          <div style={S.searchBar}>
            <Search size={14} color={HEART.textFaint} />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
              onFocus={() => setShowSearchResults(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search"
              style={S.searchInput}
            />
            {searchQuery ? (
              <button
                className="sc-searchclear"
                onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
                aria-label="Clear search"
              >
                <X size={11} />
              </button>
            ) : (
              <kbd style={S.kbd}>⌘K</kbd>
            )}
          </div>

          {showSearchResults && searchQuery && (
            <div className="sc-panelcard sc-searchresults">
              {searchResults.length > 0 ? (
                searchResults.map((item) => (
                  <button
                    key={item.id}
                    className="sc-searchresultrow"
                    onClick={() => goToSearchResult(item)}
                  >
                    <span style={{ ...S.navIconSmall, background: HEART.goldSoft, color: HEART.redDark }}>
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                ))
              ) : (
                <div className="sc-searchempty">No matches for "{searchQuery}"</div>
              )}
            </div>
          )}
        </div>

        {/* account pill + dropdown */}
        <div style={{ position: "relative" }} ref={menuRef}>
          <button className="sc-accountbtn" onClick={() => setShowAccountMenu((v) => !v)}>
            <StoreTile partner={currentPartner} />
            <span className="sc-desktop-only" style={{ fontSize: 13, fontWeight: 700, color: HEART.dark }}>
              {currentPartner.name || "My Store"}
            </span>
            <ChevronDown
              size={12}
              color={HEART.textMuted}
              style={{ transform: showAccountMenu ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
          </button>

          {showAccountMenu && (
            <div className="sc-panelcard" style={S.accountDropdown}>
              <div style={S.accountDropdownHeader}>
                <StoreTile partner={currentPartner} size={38} />
                <div style={{ minWidth: 0 }}>
                  <p style={S.truncBold}>{currentPartner.name || "My Store"}</p>
                  <p style={S.truncMuted}>{currentPartner.brandId}</p>
                </div>
              </div>

              <button className="sc-createrow" onClick={handleCreateStore}>
                <span style={{ ...S.navIconSmall, background: HEART.goldSoft, color: HEART.redDark }}>
                  <Plus size={12} />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: HEART.dark }}>Create store</span>
              </button>

              {partners.length > 0 && (
                <div style={{ maxHeight: 360, overflowY: "auto", borderTop: `1px solid ${HEART.divider}`, marginTop: 6, paddingTop: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".12em", color: HEART.textMuted, padding: "0 4px 8px" }}>
                    MY BRANDS
                  </div>
                 {partners.map((partner: CommercePartner) => (
  <StoreSwitchCard
    key={partner.brandId}
    partner={partner}
    isCurrent={partner.brandId === brandId}
  />
))}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${HEART.divider}`, marginTop: 4, paddingTop: 4 }}>
                <div style={{ padding: "8px 12px" }}>
                  <p style={S.truncBold}>{currentUser?.firstName} {currentUser?.lastName}</p>
                  <p style={S.truncMuted}>{currentUser?.email}</p>
                </div>
                <button className="sc-logout" onClick={handleLogout}>
                  <LogOut size={15} /> Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={S.body}>
        {/* ═══ DESKTOP SIDEBAR — collapsible / expandable ═══ */}
        <aside
          className="sc-desktop-only"
          style={{ ...S.sidebar, width: sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED }}
        >
          <div style={S.sidebarToggleRow}>
            {sidebarOpen && <span style={S.sidebarToggleLabel}>Menu</span>}
            <button
              className="sc-collapse-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarOpen ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
            </button>
          </div>

          <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, overflowY: "auto" }}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={`sc-navitem${activeTab === item.id ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`}
                onClick={() => handleTabChange(item.id)}
                title={item.label}
              >
                <span className="sc-navicon">{item.icon}</span>
                {sidebarOpen && item.label}
              </button>
            ))}
          </nav>

          <div style={{ borderTop: `1px solid ${HEART.divider}`, paddingTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
            <button className={`sc-settings${!sidebarOpen ? " collapsed" : ""}`} title="Settings">
              <Settings size={16} /> {sidebarOpen && "Settings"}
            </button>
            <button className={`sc-logout${!sidebarOpen ? " collapsed" : ""}`} onClick={handleLogout} title="Log out">
              <LogOut size={16} /> {sidebarOpen && "Log out"}
            </button>
          </div>
        </aside>

        {/* ═══ CONTENT ═══ */}
        <main className="sc-panelcard sc-mobile-pad" style={S.content}>
          {renderComponent()}
        </main>
      </div>

      {/* ═══ MOBILE BOTTOM TAB BAR — fixed footer, icon stacked over label per tab ═══ */}
      <div className="sc-tabbar">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            className={`sc-tab${activeTab === item.id ? " active" : ""}`}
            onClick={() => handleTabChange(item.id)}
          >
            <span className="sc-tabicon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default StoreCentral;

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: HEART.bg, color: HEART.dark },
  loaderPage: { minHeight: "100vh", background: HEART.bg, display: "flex", alignItems: "center", justifyContent: "center" },
  loaderBubble: {
    width: 56, height: 56, borderRadius: "50%", margin: "0 auto",
    background: `linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 8px 20px rgba(200,32,46,.35)",
  },
  emptyCard: {
    background: "#fff", border: `1px solid ${HEART.border}`, borderRadius: 20,
    padding: "36px 40px", textAlign: "center", boxShadow: "0 10px 30px rgba(27,14,6,.08)",
  },
  primaryBtn: {
    background: `linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, color: "#fff",
    border: "none", borderRadius: 999, padding: "12px 28px", fontWeight: 700, fontSize: 13,
    cursor: "pointer", letterSpacing: "0.04em", boxShadow: "0 8px 20px rgba(200,32,46,.3)",
  },

  topbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    padding: "10px 20px", background: HEART.panelBg, borderBottom: `1px solid ${HEART.border}`,
    position: "sticky", top: 0, zIndex: 40,
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10 },
  brandName: { fontSize: 15, fontWeight: 800, color: HEART.dark, lineHeight: 1.1 },
  brandSub: { fontSize: 11, color: HEART.textMuted, fontWeight: 600 },

  searchBar: {
    display: "flex", alignItems: "center", gap: 8, background: HEART.goldSoft, borderRadius: 999,
    padding: "8px 14px", border: `1px solid ${HEART.border}`,
  },
  searchInput: {
    flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
    fontSize: 13, color: HEART.dark, fontFamily: "inherit",
  },
  kbd: {
    background: "#fff", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontWeight: 800,
    color: HEART.textFaint, border: `1px solid ${HEART.border}`, flexShrink: 0,
  },

accountDropdown: {
  position: "absolute", right: 0, top: "calc(100% + 10px)", width: 400, maxWidth: "calc(100vw - 24px)", zIndex: 50,
  background: "#fff", borderRadius: 20, padding: 10, border: `1px solid ${HEART.border}`,
  boxShadow: "0 14px 40px rgba(27,14,6,.16)",
},
  accountDropdownHeader: {
    display: "flex", alignItems: "center", gap: 10, background: HEART.goldSoft,
    borderRadius: 14, padding: "10px 12px", marginBottom: 4,
  },
  truncBold: { fontSize: 13, fontWeight: 700, color: HEART.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  truncMuted: { fontSize: 11, color: HEART.textFaint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  navIconSmall: { width: 24, height: 24, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  body: { display: "flex", alignItems: "flex-start" },
  sidebar: {
    flexShrink: 0, flexDirection: "column", padding: 14, gap: 12,
    borderRight: `1px solid ${HEART.border}`, minHeight: "calc(100vh - 61px)",
    position: "sticky", top: 61, background: HEART.bg, transition: "width .18s ease", overflow: "hidden",
  },
  sidebarToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 4px" },
  sidebarToggleLabel: { fontSize: 11, fontWeight: 800, color: HEART.textMuted, textTransform: "uppercase", letterSpacing: ".06em" },

  content: { flex: 1, minWidth: 0, padding: "24px 20px 100px" },

};